<template>
  <section class="section">
    <BaseForm
      v-if="!success"
      ref="baseForm"
      :title="$t('knockaut.licenseRenewal')"
      :data-object="dataObject"
      :content-type="'knockaut-license-renewal'"
      :custom-submit="true"
      :on-custom-submit="enterKey"
      :has-error="errorMessage"
      :error-message="errorMessage"
      :can-save="canSave"
      :column-width="'is-8 is-offset-2'"
      :card-max-width="'1000'"
      :cardClass="'pa-2'"
    >
      <template #content>
        <div v-if="!unsubscribe">
          <h1>{{ $t('knockaut.licenseRenewal') }}</h1>
          <div class="mt-4">
            <b>Lizenzverlängerung für:</b>
            <v-chip
              class="ml-3"
              density="compact"
              variant="flat"
              color="grey-darken-3"
              >{{ licenseUsername }}</v-chip
            >
            <br />
            <div class="mt-1">
              <b>Ablaufdatum:</b>
              <v-chip
                class="ml-3"
                density="compact"
                variant="flat"
                color="grey-darken-3"
                >{{ expirationDate }}</v-chip
              >
            </div>
          </div>

          <div class="mt-4">
            <b>Bitte wählen Sie die Laufzeit:</b>
          </div>
          <div class="d-flex justify-space-evenly mb-6 mt-4">
            <v-card
              v-for="license in licenses"
              :key="license.length"
              :color="license.color"
              :style="[
                selectedLicense?.length === license.length
                  ? { border: `0.2rem solid ${selectedLicense.color}` }
                  : { border: `0.2rem solid #f5f5f5` },
              ]"
              flat
              @click="selectLicense(license)"
            >
              <v-card-title class="text-center">
                <span class="plan-price-amount">{{ license.length }}</span>
                Jahr{{ license.length === 1 ? '' : 'e' }}
              </v-card-title>
              <v-list bg-color="grey-lighten-3">
                <v-list-item density="compact">
                  <strong
                    >{{ parseFloat(license.price).toFixed(2) }}
                    {{ license.currency }}</strong
                  >
                  (exkl. {{ parseFloat(license.vat_rate).toFixed(1) }}% MWST)
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item density="compact">{{
                  license.description
                }}</v-list-item>
                <v-divider></v-divider>
                <v-list-item density="compact">
                  {{ pricePerMonth(license) }} {{ license.currency }} / Monat
                </v-list-item>
              </v-list>
              <v-card-actions>
                <v-btn
                  :ripple="false"
                  variant="text"
                  block
                  @click="selectLicense(license)"
                >
                  Auswählen
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>
        </div>

        <v-alert class="mt-4" v-if="unsubscribe" :closable="false">
          Sind Sie sicher, dass sie ihre Lizenz nicht verlängern möchten?
        </v-alert>
        <v-alert
          class="mt-4"
          icon="mdi-lightbulb-on-outline"
          title="Vorteile des Connect- & Care-Dienstes"
          :closable="false"
        >
          <div class="mt-2">
            <v-list-item
              v-for="benefit in connectAndCareBenefits"
              :key="benefit"
              prepend-icon="mdi-check"
              density="compact"
            >
              {{ benefit }}
            </v-list-item>

            <div v-if="unsubscribe">
              <v-btn class="is-fullwidth" @click="unsubscribe = false">
                Zur Lizenzverlängerung
              </v-btn>
              <v-btn
                class="button is-fullwidth mt-2"
                @click="disableReminderMail()"
              >
                Erinnerungsmail abbestellen
              </v-btn>
            </div>
          </div>
        </v-alert>

        <div v-if="!unsubscribe">
          <v-row class="mt-4" cols="12" md="6">
            <v-col>
              <v-text-field
                v-model.trim="assignedBy"
                label="Lizenzverlängerung gelöst von"
                placeholder="Ihr Name"
                icon="text-short"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-if="!odooCustomerId"
                v-model.trim="odooCustomerEmail"
                :disabled="!!odooCustomerId"
                label="E-Mail für Rechnung"
                placeholder="name@kunde.ch"
                type="email"
                prepend-inner-icon="email"
              />
            </v-col>
          </v-row>

          <v-alert
            v-if="isBrelagSupporter"
            type="warning"
            class="mt-4"
            has-icon
            icon="lightbulb-on-outline"
            :closable="false"
          >
            <div class="mt-2">
              <h3>Info für Brelag Supporter</h3>
              Wenn die Lizenz nicht sofort gelöst werden soll, kann im
              <a
                href="https://styler.brelag.com/admin/brelag/knockautproject/"
                target="_blank"
                >Styler Admin</a
              >
              die Odoo Id des Kunden hinterlegt werden damit der Kunde die
              Erinnerungsmail bekommt.
              <template v-if="odooCustomerId">
                <p>Odoo KundenId ID: {{ odooCustomerId }}</p>
              </template>
              <v-btn class="mt-2" @click="searchForCustomerId()">
                Odoo ID anhand von E-Mail suchen
              </v-btn>
            </div>
          </v-alert>

          <div class="mt-2">
            Nach dem Absenden des Formulars wird die Lizenz auf Ihrem Knockaut
            Controller automatisch verlängert, und Sie erhalten im Anschluss
            eine Rechnung per E-Mail.
          </div>

          <div class="mt-2">
            Mit dem Abschluss der Lizenzerneuerung erklären Sie sich mit den
            <a href="https://www.brelag.com/agb" target="_blank"
              >Allgemeinen Geschäfts- und Lieferbedingungen (AGB)</a
            >
            sowie mit der
            <a
              href="https://www.brelag.com/datenschutzbestimmungen"
              target="_blank"
              >BRELAG Datenschutzerklärung</a
            >
            einverstanden.
          </div>

          <div>
            <v-checkbox v-model="agbAccepted">
              <template #label>
                Ich habe die AGB und Datenschutzbestimmungen gelesen und
                akzeptiere diese.
              </template>
            </v-checkbox>
          </div>
        </div>
      </template>
    </BaseForm>
    <div v-else>
      <v-alert
        v-if="unsubscribe"
        :closable="false"
        title="Erfolgreich abbestellt"
        type="success"
        has-icon
        >Erinnerungsmail wurde erfolgreich abbestellt. Sie können dieses Fenster
        schliessen.</v-alert
      >
      <v-alert
        v-else
        :closable="false"
        title="Lizenz erneuert"
        type="success"
        has-icon
        >Lizenz wurde erfolgreich erneuert. Sie können dieses Fenster
        schliessen.</v-alert
      >
    </div>
  </section>
  <LoadingOverlay v-model:loading="loading" />
</template>

<script setup lang="ts">
import moment from 'moment'
import { computed, onMounted, onUnmounted, Ref, ref } from 'vue'
import { useRoute } from 'vue-router'

import { apiClient } from '@/api/ApiClient'
import { apiClientV2 } from '@/api/ApiClientV2'
import { KnockautProject } from '@/apps/brelag/common/models/knockaut'
import {
  LICENSE_OFFERS,
  LicenseLength,
  LicenseOffer,
} from '@/apps/brelag/common/models/knockautLicenseKey'
import BaseForm from '@/components/common/BaseForm.vue'
import LoadingOverlay from '@/components/common/LoadingOverlay.vue'
import { useNavigationVisibilityStore } from '@/store/NavigationVisibilityStore'

const route = useRoute()
const navigationVisibilityStore = useNavigationVisibilityStore()
const dataObject = ref({})
const baseForm = ref(null)
const projectId: Ref<string> = ref(null)
const validationCode: Ref<string> = ref(null)
const odooCustomerId: Ref<number> = ref(null)
const licenseUsername: Ref<string> = ref(null)
const assignedBy: Ref<string> = ref(null)
const unsubscribe: Ref<boolean> = ref(false)
const isBrelagSupporter = ref(false)

const odooCustomerEmail: Ref<string> = ref(null)
const subscriptionLength: Ref<LicenseLength> = ref(1)

const errorMessage: Ref<string> = ref(null)
const agbAccepted = ref(false)
const success = ref(false)
const selectedLicense: Ref<LicenseOffer> = ref(null)
const licenses: Ref<LicenseOffer[]> = ref(LICENSE_OFFERS)
const loading = ref(false)

const connectAndCareBenefits = [
  'Versions- und Sicherheitsupdates',
  'Fernzugriff (Connect-Dienst)',
  'Zwingend notwendig für Integration von KnockautX-Komponenten',
  'Verwaltung über das STYLER Planungs- & Projektierungstool',
  'Versenden von Push-Nachrichten',
  'Fernwartungsmöglichkeit durch Brelag',
  'Tägliches Auto-Backup des Gesamtsystems',
  'Täglicher Health-Check des Controllers zwecks Erreichbarkeit des Systems',
]

function selectLicense(licenseOffer: LicenseOffer) {
  selectedLicense.value = licenseOffer
  subscriptionLength.value = licenseOffer.length
}

onMounted(async () => {
  loading.value = true

  try {
    navigationVisibilityStore.setAppMenuNotVisible()
    // Get URL query
    projectId.value = route.query.project_id as string
    validationCode.value = route.query.validation_code as string
    odooCustomerId.value = parseInt(route.query.customer_id as string)

    licenseUsername.value = decodeURIComponent(
      route.query.license_username as string
    )

    if (!odooCustomerId.value) {
      await loadCustomerEmailFromLicense()
    }

    if (route.query.unsubscribe && route.query.unsubscribe === 'true') {
      unsubscribe.value = true
    }

    if (
      route.query.is_brelag_supporter &&
      route.query.is_brelag_supporter === 'true'
    ) {
      isBrelagSupporter.value = true
    }

    if (!projectId.value || !validationCode.value) {
      errorMessage.value = 'URL enthält keine Projekt Informationen'
    }
  } catch (error) {
    baseForm.value.handleError(error)
  }
  loading.value = false
})

onUnmounted(() => {
  navigationVisibilityStore.setAppMenuVisible()
})

async function loadCustomerEmailFromLicense() {
  try {
    const response = await apiClient.tryGetOdooCustomer(licenseUsername.value)
    odooCustomerEmail.value = response.data.customer_email
    if (
      odooCustomerId.value &&
      response.data.customer_id !== odooCustomerId.value
    ) {
      throw new Error(`Odoo Kunden ID ${odooCustomerId.value} nicht gültig!`)
    } else {
      odooCustomerId.value = response.data.customer_id
    }
  } catch (error) {
    // License not assigned yet
  }
}

async function searchForCustomerId() {
  loading.value = true
  try {
    baseForm.value.clearError()
    await loadCustomerFromEmail()
  } catch (error) {
    baseForm.value.handleError(error)
  }
  loading.value = false
}

async function loadCustomerFromEmail() {
  const response = await apiClient.tryGetOdooCustomer(
    licenseUsername.value,
    odooCustomerEmail.value
  )
  if (response.data.customer_email !== odooCustomerEmail.value) {
    throw new Error('Odoo customer_email not valid!')
  }
  odooCustomerId.value = response.data.customer_id
}

function enterKey() {
  requestLicenseRenewal()
}

async function requestLicenseRenewal() {
  loading.value = true

  try {
    if (odooCustomerEmail.value && !odooCustomerId.value) {
      await loadCustomerFromEmail()
    }

    if (!odooCustomerId.value) {
      throw new Error('Keine Customer ID definiert!')
    }

    await apiClient.requestLicenseRenewal(
      projectId.value,
      validationCode.value,
      subscriptionLength.value,
      assignedBy.value,
      odooCustomerId.value
    )
    baseForm.value.clearError()
    success.value = true
  } catch (error) {
    baseForm.value.handleError(error)
  }
  loading.value = false
}

async function disableReminderMail() {
  loading.value = true

  try {
    await KnockautProject.unsubscribeConnectAndCareReminder(
      apiClientV2,
      projectId.value
    )
    baseForm.value.clearError()
    success.value = true
  } catch (error) {
    baseForm.value.handleError(error)
  }
  loading.value = false
}

function pricePerMonth(licenseOffer: LicenseOffer) {
  return (licenseOffer.price / licenseOffer.months).toFixed(2)
}

const expirationDate = computed((): string => {
  return moment(parseInt(validationCode.value) * 1000).format(
    'DD.MM.YYYY HH:mm'
  )
})

const canSave = computed((): boolean => {
  return (
    agbAccepted.value &&
    !!assignedBy.value &&
    (!!odooCustomerEmail.value || !!odooCustomerId.value) &&
    !!selectedLicense.value &&
    !errorMessage.value
  )
})
</script>

<style lang="scss" scoped>
.plan-price-amount {
  font-size: 3.1rem !important;
  font-weight: 700;
}

.pricing-table .pricing-plan .plan-price {
  font-size: 1.8rem !important;
}
</style>
